import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Landing from "../../components/Landing";

const reveal = {
  hidden: { opacity: 0, y: 45 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};

const Feature = ({ eyebrow, title, copy, image, link, reverse = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [-35, 35]);

  return (
    <motion.article
      ref={ref}
      variants={reveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={`grid items-center gap-9 lg:grid-cols-2 lg:gap-20 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
    >
      <div className="image-frame relative h-[430px] overflow-hidden sm:h-[560px]">
        <motion.img src={image} alt={title} style={{ y: imageY }} className="absolute -top-10 h-[calc(100%+80px)] w-full object-cover" />
        <span className="absolute bottom-5 left-5 border border-white/35 bg-[#14274A]/80 px-4 py-2 font-montserrat text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur-sm">
          Zeebar moments
        </span>
      </div>
      <div className="max-w-xl px-2 lg:px-0">
        <p className="section-eyebrow">{eyebrow}</p>
        <h2 className="section-title">{title}</h2>
        <div className="my-6 h-px w-16 bg-[#E0B973]" />
        <p className="section-copy">{copy}</p>
        <Link to={link} className="text-link mt-8 inline-flex items-center gap-3">
          Discover more <span aria-hidden="true">→</span>
        </Link>
      </div>
    </motion.article>
  );
};

export default function Home() {
  return (
    <div className="overflow-hidden bg-[#F8F5EF] text-[#14274A]">
      <Landing />

      <section className="relative px-6 py-24 sm:px-10 lg:py-32">
        <div className="absolute right-0 top-0 hidden h-full w-1/3 bg-white/50 lg:block" />
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-end"
        >
          <div>
            <p className="section-eyebrow">Welcome to Zeebar</p>
            <h2 className="font-garamond text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">A quieter kind of luxury.</h2>
          </div>
          <div className="border-l border-[#E0B973]/60 pl-7 lg:pl-12">
            <p className="section-copy max-w-2xl text-lg">
              Rooted in the artistry, culture and heritage of traditional Bhutanese architecture, Zeebar pairs a warm sense of place with the ease of modern hospitality.
            </p>
            <div className="mt-9 grid grid-cols-3 gap-4 border-t border-[#14274A]/10 pt-7">
              {[['Bhutanese', 'Inspired'], ['Thoughtful', 'Comfort'], ['Warm', 'Hospitality']].map(([top, bottom]) => (
                <div key={top}><strong className="block font-garamond text-xl font-medium sm:text-2xl">{top}</strong><span className="font-montserrat text-[9px] uppercase tracking-[0.2em] text-[#14274A]/55">{bottom}</span></div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      <section className="mx-auto max-w-7xl space-y-24 px-6 pb-28 sm:px-10 lg:space-y-36 lg:pb-40">
        <Feature
          eyebrow="Stay well"
          title="Rooms made for slowing down"
          copy="Step away from the everyday and settle into a private retreat. Calm tones, generous comfort and considered details create room to rest, reconnect and simply be. Every stay includes a complimentary breakfast."
          image="/images/general/Suite_2.png"
          link="/rooms"
        />
        <Feature
          reverse
          eyebrow="Find your flow"
          title="Serenity, poolside"
          copy="Leave your worries at the water's edge. Our pristine swimming pool invites unhurried afternoons, effortless floating and the rare feeling that time has finally decided to stand still."
          image="/images/general/landing2.png"
          link="/facilities"
        />
      </section>

      <section className="relative h-[72vh] min-h-[580px] overflow-hidden">
        <div className="parallax-cta absolute inset-[-12%]" style={{ backgroundImage: "url('/images/general/pool3.jpg')" }} />
        <div className="absolute inset-0 bg-[#14274A]/65" />
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }} className="relative z-[1] flex h-full flex-col items-center justify-center px-6 text-center text-white">
          <p className="section-eyebrow !text-[#E0B973]">Your Bhutan escape awaits</p>
          <h2 className="max-w-3xl font-garamond text-5xl leading-none sm:text-6xl lg:text-7xl">Come for the calm.<br /><span className="italic text-[#E0B973]">Stay for the feeling.</span></h2>
          <Link to="/booking" className="gold-button mt-9">Reserve your stay</Link>
        </motion.div>
      </section>
    </div>
  );
}
